import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isEqual from 'lodash/isEqual';

import {
  OpenClassBookingOrderDetails,
  OpenClassBookingOrderParticipant,
  OpenClassCertificateType,
  OpenClassRegistrationSelectionParams,
} from '../../openClasses/types/OpenClass';
import { registrationHasRecordedOutcome } from '../../openClasses/utils/certificateEligibility';
import { isValidEmail } from '../../utils/email';

export type RegistrationDraft = {
  key: string;
  registrationId?: number;
  openClassId: number;
  certificateTemplateId: number;
  // Carried next to the template id so the picker can still name a certificate whose class
  // isn't in view
  certificateType: OpenClassCertificateType;
  sessionIds: number[];
};

export type ParticipantDraft = {
  key: string;
  participantId?: number;
  hasRecordedOutcome?: boolean;
  name: string;
  email: string;
  // ISO YYYY-MM-DD, empty means unset.
  birthDate: string;
  birthPlace: string;
  registrations: RegistrationDraft[];
};

export type ParticipantDetailsChange = {
  participantId: number;
  name: string;
  email: string;
  birthDate: string;
  birthPlace: string;
};

export type RegistrationChangeSet = {
  participantId: number;
  remove: number[];
  update: {
    registrationId: number;
    selection: OpenClassRegistrationSelectionParams;
    certificateType: OpenClassCertificateType;
  }[];
  create: OpenClassRegistrationSelectionParams[];
};

export type ParticipantSavePlan = {
  removeParticipantIds: number[];
  detailChanges: ParticipantDetailsChange[];
  changeSets: RegistrationChangeSet[];
  addRows: ParticipantDraft[];
};

const draftKeyPrefix = Math.random().toString(36).slice(2, 10);
let draftKeyCounter = 0;
export const newDraftKey = (): string =>
  `draft-${draftKeyPrefix}-${++draftKeyCounter}`;

dayjs.extend(customParseFormat);

// The API only accepts ISO YYYY-MM-DD, while a CSV import puts arbitrary text in the field.
export const isIsoCalendarDate = (value: string): boolean =>
  dayjs(value, 'YYYY-MM-DD', true).isValid();

export const emptyParticipantDraft = (): ParticipantDraft => ({
  key: newDraftKey(),
  hasRecordedOutcome: false,
  name: '',
  email: '',
  birthDate: '',
  birthPlace: '',
  registrations: [],
});

export const draftFromParticipant = (
  participant: OpenClassBookingOrderParticipant,
): ParticipantDraft => ({
  key: `p-${participant.id}`,
  participantId: participant.id,
  hasRecordedOutcome: participant.registrations.some(
    registrationHasRecordedOutcome,
  ),
  name: participant.attendee.name,
  email: participant.attendee.email ?? '',
  birthDate: participant.attendee.birthDate ?? '',
  birthPlace: participant.attendee.birthPlace ?? '',
  registrations: participant.registrations.map((registration) => ({
    key: `reg-${registration.id}`,
    registrationId: registration.id,
    openClassId: registration.openClassId,
    certificateTemplateId: registration.certificateTemplateId,
    certificateType: registration.certificateType,
    sessionIds: (registration.sessionAttendances ?? []).map(
      (attendance) => attendance.sessionId,
    ),
  })),
});

export const draftsFromDetails = (
  details?: OpenClassBookingOrderDetails,
): ParticipantDraft[] =>
  (details?.participants ?? []).map(draftFromParticipant);

export const draftRequiresBirthDetails = (draft: ParticipantDraft): boolean =>
  draftCertificateTypes(draft).includes('furbo');

export const draftMissesBirthDetails = (draft: ParticipantDraft): boolean =>
  draftRequiresBirthDetails(draft) &&
  (draft.birthDate.trim().length === 0 || draft.birthPlace.trim().length === 0);

export const draftHasInvalidBirthDate = (draft: ParticipantDraft): boolean =>
  draft.birthDate.trim().length > 0 &&
  !isIsoCalendarDate(draft.birthDate.trim());

export const isParticipantDraftValid = (draft: ParticipantDraft): boolean =>
  draft.name.trim().length > 0 &&
  (draft.email.trim().length === 0 || isValidEmail(draft.email)) &&
  !draftMissesBirthDetails(draft) &&
  !draftHasInvalidBirthDate(draft);

const sortedSessionIds = (sessionIds: number[]): number[] =>
  [...sessionIds].sort((a, b) => a - b);

export const registrationDraftToSelection = (
  registration: RegistrationDraft,
): OpenClassRegistrationSelectionParams => ({
  openClassId: registration.openClassId,
  certificateTemplateId: registration.certificateTemplateId,
  sessionIds: sortedSessionIds(registration.sessionIds),
});

export const draftCertificateTypes = (
  draft: Pick<ParticipantDraft, 'registrations'>,
): OpenClassCertificateType[] =>
  Array.from(
    new Set(draft.registrations.map(({ certificateType }) => certificateType)),
  );

const registrationDraftsEqual = (
  a: RegistrationDraft,
  b: RegistrationDraft,
): boolean =>
  a.registrationId === b.registrationId &&
  isEqual(registrationDraftToSelection(a), registrationDraftToSelection(b));

const participantDraftEqual = (
  a: ParticipantDraft,
  b: ParticipantDraft,
): boolean =>
  a.participantId === b.participantId &&
  a.name === b.name &&
  a.email === b.email &&
  a.birthDate === b.birthDate &&
  a.birthPlace === b.birthPlace &&
  a.registrations.length === b.registrations.length &&
  a.registrations.every((registration, index) =>
    registrationDraftsEqual(registration, b.registrations[index]),
  );

export const participantDraftsEqual = (
  a: ParticipantDraft[],
  b: ParticipantDraft[],
): boolean =>
  a.length === b.length &&
  a.every((draft, index) => participantDraftEqual(draft, b[index]));

export const changedParticipantKeys = (
  drafts: ParticipantDraft[],
  originals: ParticipantDraft[],
): string[] => {
  const originalsById = new Map(
    originals.map((draft) => [draft.participantId, draft]),
  );
  return drafts
    .filter((draft) => {
      const original = originalsById.get(draft.participantId);
      return !original || !participantDraftEqual(draft, original);
    })
    .map(({ key }) => key);
};

export const applyAssignmentToDrafts = (
  drafts: ParticipantDraft[],
  selectedKeys: string[],
  selection: OpenClassRegistrationSelectionParams,
  certificateType: OpenClassCertificateType,
): ParticipantDraft[] =>
  drafts.map((draft) => {
    if (draft.hasRecordedOutcome || !selectedKeys.includes(draft.key)) {
      return draft;
    }

    const existing = draft.registrations.find(
      (registration) => registration.openClassId === selection.openClassId,
    );
    return {
      ...draft,
      registrations: existing
        ? draft.registrations.map((registration) =>
            registration === existing
              ? {
                  ...registration,
                  certificateTemplateId: selection.certificateTemplateId,
                  certificateType,
                  sessionIds: selection.sessionIds,
                }
              : registration,
          )
        : [
            ...draft.registrations,
            {
              key: newDraftKey(),
              openClassId: selection.openClassId,
              certificateTemplateId: selection.certificateTemplateId,
              certificateType,
              sessionIds: selection.sessionIds,
            },
          ],
    };
  });

const registrationChangeSet = (
  draft: ParticipantDraft,
  original: ParticipantDraft,
): RegistrationChangeSet | null => {
  const remove: number[] = [];
  const update: RegistrationChangeSet['update'] = [];
  const create: OpenClassRegistrationSelectionParams[] = [];

  original.registrations.forEach((registration) => {
    const kept = draft.registrations.find(
      (candidate) => candidate.registrationId === registration.registrationId,
    );
    if (!kept || kept.openClassId !== registration.openClassId) {
      remove.push(registration.registrationId!);
    } else if (!registrationDraftsEqual(kept, registration)) {
      update.push({
        registrationId: registration.registrationId!,
        selection: registrationDraftToSelection(kept),
        certificateType: kept.certificateType,
      });
    }
  });

  draft.registrations.forEach((registration) => {
    const original_ = original.registrations.find(
      (candidate) => candidate.registrationId === registration.registrationId,
    );
    if (
      registration.registrationId == null ||
      !original_ ||
      original_.openClassId !== registration.openClassId
    ) {
      create.push(registrationDraftToSelection(registration));
    }
  });

  if (remove.length === 0 && update.length === 0 && create.length === 0) {
    return null;
  }
  return { participantId: original.participantId!, remove, update, create };
};

export const buildParticipantSavePlan = (
  drafts: ParticipantDraft[],
  originals: ParticipantDraft[],
): ParticipantSavePlan => {
  const draftIds = new Set(
    drafts
      .map((draft) => draft.participantId)
      .filter((id): id is number => id != null),
  );
  const removeParticipantIds = originals
    .filter((original) => !draftIds.has(original.participantId!))
    .map((original) => original.participantId!);

  const detailChanges: ParticipantDetailsChange[] = [];
  const changeSets: RegistrationChangeSet[] = [];
  drafts.forEach((draft) => {
    if (draft.participantId == null) {
      return;
    }
    const original = originals.find(
      (candidate) => candidate.participantId === draft.participantId,
    );
    if (!original) {
      return;
    }
    if (
      draft.name.trim() !== original.name ||
      draft.email.trim() !== original.email ||
      draft.birthDate.trim() !== original.birthDate ||
      draft.birthPlace.trim() !== original.birthPlace
    ) {
      detailChanges.push({
        participantId: draft.participantId,
        name: draft.name.trim(),
        email: draft.email.trim(),
        birthDate: draft.birthDate.trim(),
        birthPlace: draft.birthPlace.trim(),
      });
    }
    const changeSet = registrationChangeSet(draft, original);
    if (changeSet) {
      changeSets.push(changeSet);
    }
  });

  return {
    removeParticipantIds,
    detailChanges,
    changeSets,
    addRows: drafts.filter((draft) => draft.participantId == null),
  };
};

export const updateDropsFurboRequirement = (
  update: RegistrationChangeSet['update'][number],
): boolean => update.certificateType !== 'furbo';

// The backend wipes the sent stamp when the certificate type changes, so these saves need an
// explicit ok first
export const attendeesLosingSentStamp = (
  plan: ParticipantSavePlan,
  participants: OpenClassBookingOrderParticipant[],
): string[] => {
  const updatedTypes = new Map(
    plan.changeSets.flatMap(({ update }) =>
      update.map(({ registrationId, selection }) => [
        registrationId,
        selection.certificateTemplateId,
      ]),
    ),
  );
  return participants
    .filter((participant) =>
      participant.registrations.some(
        (registration) =>
          registration.certificateSentAt != null &&
          updatedTypes.has(registration.id) &&
          updatedTypes.get(registration.id) !==
            registration.certificateTemplateId,
      ),
    )
    .map((participant) => participant.attendee.name);
};

export const mergeFailedDrafts = (
  serverDrafts: ParticipantDraft[],
  failedRows: ParticipantDraft[],
): ParticipantDraft[] => {
  const failedById = new Map(
    failedRows
      .filter((row) => row.participantId != null)
      .map((row) => [row.participantId!, row]),
  );
  return [
    ...serverDrafts.map(
      (draft) => failedById.get(draft.participantId ?? -1) ?? draft,
    ),
    ...failedRows.filter((row) => row.participantId == null),
  ];
};
